import { Component, OnInit, ViewChild, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';
import { HsHomepageService } from '../hs-homepage.service';
import { EmployerEvent, EmployerEventOptions, HsEmployerEventOptions } from 'src/app/shared/interfaces/employer-event';
import { HsHomepageEvent } from 'src/app/shared/interfaces/homepage-event';
import { HsHomepageEventFilter } from 'src/app/shared/interfaces/employer-event-filter';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { SharedService } from 'src/app/core/services/shared.service';
import { DATE_FORMAT, ERROR_GETTING_DATA } from 'src/app/app.constants';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';
import { EmployerEventsFormComponent } from 'src/app/employer/employer-events/employer-events-form/employer-events-form.component';
import { EventModalMode } from 'src/app/shared/enums/event-modal-mode';
import { EmployerService } from 'src/app/employer/employer.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';

@Component({
  selector: 'hs-events',
  templateUrl: './hs-events.component.html',
  styleUrls: ['./hs-events.component.scss'],
})
export class HsEventsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Output() tabChanged = new EventEmitter<string>();
  @Output() filterDateChanged = new EventEmitter<Date>();

  constructor(
    private readonly hsHomepageService: HsHomepageService,
    private readonly cdr: ChangeDetectorRef,
    public sharedService: SharedService,
    private readonly dialog: MatDialog,
    private readonly employerService: EmployerService,
    private readonly snackBar: MatSnackBar,
    private readonly route: ActivatedRoute,
    private readonly location: Location
  ) {}

  staffId: number;
  permissions: { canEditEvent: boolean; canDeleteEvent: boolean } = {
    canEditEvent: false,
    canDeleteEvent: false,
  };

  loading = false;
  runSetUp = false;
  eventError = false;

  openEvent = false;
  loadingEvent: HsHomepageEvent | null = null;

  eventColumns = {
    'Missing Appraisal': ['rootName', 'type'],
    Overdue: ['rootName', 'type', 'plannedEvent', 'plannedDate'],
    Upcoming: ['rootName', 'type', 'plannedEvent', 'plannedDate'],
    Errors: ['rootName', 'type'],
    ELI: ['rootName', 'type', 'plannedEvent', 'plannedDate'],
  };

  filterOptions: HsEmployerEventOptions = {
    types: [],
    dateFrom: null,
    subcontractors: [],
    assessors: [],
  };
  eventsDataSource = new MatTableDataSource<HsHomepageEvent>();

  filterType: number[] = [];
  filterDateFrom: Date | null = null;
  filterSubcontractorId: number | null = null;
  filterAssessorId: number | null = null;

  dateColumns = ['nextDueDate', 'actualDate', 'plannedDate'];
  dateFormat = DATE_FORMAT;
  dataError = ERROR_GETTING_DATA;

  totalEvents = 0;
  pageSize = 50;
  pageIndex = 0;

  tabs = ['Missing Appraisal', 'Overdue', 'Upcoming', 'Errors', 'ELI'];
  activeTab = 'Missing Appraisal';

  ngOnInit(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab !== null) {
      switch (tab) {
        case 'overdue':
          this.activeTab = 'Overdue';
          break;
        case 'upcoming':
          this.activeTab = 'Upcoming';
          break;
        case 'eli':
          this.activeTab = 'ELI';
          break;
        case 'errors':
          this.activeTab = 'Errors';
          break;
        case 'missing-appraisal':
          this.activeTab = 'Missing Appraisal';
          break;
        default:
          this.activeTab = 'Missing Appraisal';
          break;
      }
      window.history.pushState({}, '', `hs-homepage?tab=${tab.toLowerCase()}`);
    }

    // Subscribe for back and forward navigation changes
    this.location.subscribe((event) => {
      const params = event.url.split('?')[1];
      const tabName = params?.split('tab=')[1];
      switch (tabName) {
        case 'overdue':
          this.activeTab = 'Overdue';
          this.getEvents();
          this.tabChanged.emit(this.activeTab);
          break;
        case 'upcoming':
          this.activeTab = 'Upcoming';
          this.getEvents();
          this.tabChanged.emit(this.activeTab);
          break;
        case 'eli':
          this.activeTab = 'ELI';
          this.getELI();
          this.tabChanged.emit(this.activeTab);
          break;
        case 'errors':
          this.activeTab = 'Errors';
          this.getErrors();
          this.tabChanged.emit(this.activeTab);
          break;
        case 'missing-appraisal':
        default:
          this.activeTab = 'Missing Appraisal';
          this.getMissingAppraisal();
          this.tabChanged.emit(this.activeTab);
          break;
      }
    });
  }

  setUp(staffId: number, permissions: { canEditEvent: boolean; canDeleteEvent: boolean }): void {
    this.staffId = staffId;
    this.permissions = permissions;

    if (this.activeTab === 'ELI') {
      this.getELI(false);
    } else if (this.activeTab === 'Missing Appraisal') {
      this.getMissingAppraisal(false);
    } else if (this.activeTab === 'Errors') {
      this.getErrors(false);
    } else {
      this.getEvents(false);
    }
    this.runSetUp = true;
    this.tabChanged.emit(this.activeTab);
  }

  handlePageEvent($event: PageEvent): void {
    this.pageIndex = $event.pageIndex;

    if (this.activeTab === 'ELI') {
      this.getELI(false);
    } else if (this.activeTab === 'Missing Appraisal') {
      this.getMissingAppraisal(false);
    } else if (this.activeTab === 'Errors') {
      this.getErrors(false);
    } else {
      this.getEvents(false);
    }
  }

  getEvents(resetPage = true): void {
    if (this.loading) {
      return;
    }

    if (this.staffId === undefined) {
      console.error('Staff ID is required');
      return;
    }

    if (resetPage) {
      this.pageIndex = 0;
      this.paginator.pageIndex = 0;
    }

    this.loading = true;
    this.eventError = false;

    const filters: HsHomepageEventFilter = {
      type: this.filterType,
      tab: this.activeTab,
      dateFrom: this.filterDateFrom ? this.filterDateFrom.toISOString() : null,
      subcontractorId: this.filterSubcontractorId,
    };
    if (this.filterAssessorId !== null) {
      filters.assessorId = this.filterAssessorId;
    }

    this.eventsDataSource.data = [];

    this.hsHomepageService.getEvents(this.staffId, filters, this.pageIndex, this.pageSize).subscribe({
      next: (data) => {
        this.eventsDataSource.data = data.list;
        this.totalEvents = data.total;
      },
      error: (error) => {
        console.error(error);
        this.eventError = true;
        this.loading = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  getELI(resetPage = true): void {
    if (this.loading) {
      return;
    }

    if (this.staffId === undefined) {
      console.error('Staff ID is required');
      return;
    }

    if (resetPage) {
      this.pageIndex = 0;
      this.paginator.pageIndex = 0;
    }

    this.loading = true;
    this.eventError = false;

    const filters: HsHomepageEventFilter = {
      type: this.filterType,
      tab: this.activeTab,
      dateFrom: this.filterDateFrom ? this.filterDateFrom.toISOString() : null,
      subcontractorId: this.filterSubcontractorId,
    };
    if (this.filterAssessorId !== null) {
      filters.assessorId = this.filterAssessorId;
    }

    this.eventsDataSource.data = [];

    this.hsHomepageService.getELI(this.staffId, filters, this.pageIndex, this.pageSize).subscribe({
      next: (data) => {
        this.eventsDataSource.data = data.list;
        this.totalEvents = data.total;
      },
      error: (error) => {
        console.error(error);
        this.eventError = true;
        this.loading = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  getMissingAppraisal(resetPage = true): void {
    if (this.loading) {
      return;
    }

    if (this.staffId === undefined) {
      console.error('Staff ID is required');
      return;
    }

    if (resetPage) {
      this.pageIndex = 0;
      this.paginator.pageIndex = 0;
    }

    this.loading = true;
    this.eventError = false;

    const filters: HsHomepageEventFilter = {
      type: this.filterType,
      tab: this.activeTab,
      dateFrom: this.filterDateFrom ? this.filterDateFrom.toISOString() : null,
      subcontractorId: this.filterSubcontractorId,
    };
    if (this.filterAssessorId !== null) {
      filters.assessorId = this.filterAssessorId;
    }

    this.eventsDataSource.data = [];

    this.hsHomepageService.getMissingAppraisal(this.staffId, filters, this.pageIndex, this.pageSize).subscribe({
      next: (data) => {
        this.eventsDataSource.data = data.list;
        this.totalEvents = data.total;
      },
      error: (error) => {
        console.error(error);
        this.eventError = true;
        this.loading = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  getErrors(resetPage = true): void {
    if (this.loading) {
      return;
    }

    if (this.staffId === undefined) {
      console.error('Staff ID is required');
      return;
    }

    if (resetPage) {
      this.pageIndex = 0;
      this.paginator.pageIndex = 0;
    }

    this.loading = true;
    this.eventError = false;

    const filters: HsHomepageEventFilter = {
      type: this.filterType,
      tab: this.activeTab,
      dateFrom: this.filterDateFrom ? this.filterDateFrom.toISOString() : null,
      subcontractorId: this.filterSubcontractorId,
    };
    if (this.filterAssessorId !== null) {
      filters.assessorId = this.filterAssessorId;
    }

    this.eventsDataSource.data = [];

    this.hsHomepageService.getErrors(this.staffId, filters, this.pageIndex, this.pageSize).subscribe({
      next: (data) => {
        this.eventsDataSource.data = data.list;
        this.totalEvents = data.total;
      },
      error: (error) => {
        console.error(error);
        this.eventError = true;
        this.loading = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  setEventOptions(options: HsEmployerEventOptions): void {
    this.filterOptions = { ...options };
  }

  applyFilters(
    typeIds: number[],
    dateFrom: Date | null,
    subcontractorId: number | null,
    assessorId: number | null,
    resetPage = true
  ): void {
    this.filterType = [...typeIds];
    this.filterDateFrom = dateFrom;
    this.filterSubcontractorId = subcontractorId;
    this.filterAssessorId = assessorId;

    if (!this.runSetUp) {
      return;
    }

    if (this.activeTab === 'ELI') {
      this.getELI(resetPage);
    } else if (this.activeTab === 'Missing Appraisal') {
      this.getMissingAppraisal(resetPage);
    } else if (this.activeTab === 'Errors') {
      this.getErrors(resetPage);
    } else {
      this.getEvents(resetPage);
    }
  }

  addTime(element: { startTime: string | null; endTime: string | null }): string {
    let time = `, ${element.startTime}`;
    if (element.endTime) {
      time += ` - ${element.endTime}`;
    }
    return time;
  }

  handleOpenEventForm(element: HsHomepageEvent, eventId: number): void {
    if (this.openEvent) {
      return;
    }

    this.openEvent = true;
    this.loadingEvent = element;

    const subscription = this.employerService.getEventFilterOptions(element.rootId, this.staffId).subscribe({
      next: (data: EmployerEventOptions) => {
        const eventOptions: EmployerEventOptions = data;
        eventOptions.types = this.filterOptions.types;
        if (eventId !== null) {
          this.viewEvent(eventId, eventOptions);
        } else {
          this.setUpCreateEvent(element, eventOptions);
        }
      },
      error: (error) => {
        console.error(error);
        subscription.unsubscribe();
        this.loadingEvent = null;
        this.snackBar.open(this.dataError, '', {
          duration: 2000,
        });
      },
      complete: () => {
        subscription.unsubscribe();
      },
    });
  }

  setUpCreateEvent(element: HsHomepageEvent, options: EmployerEventOptions = null): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextDueDate = new Date(element.nextDueDate);
    nextDueDate.setHours(0, 0, 0, 0);

    const newEvent: EmployerEvent = {
      id: null,
      branchId: undefined,
      branchName: undefined,
      isRoot: true,
      typeGroup: undefined,
      type: this.filterOptions.types.find((t) => t.id === element.typeId),
      notes: undefined,
      assignee: { id: undefined, name: undefined },
      plannedDate: nextDueDate < today ? today : nextDueDate,
      actualDate: undefined,
      startTime: undefined,
      endTime: undefined,
      editedBy: undefined,
      editedDate: undefined,
      outlookEvent: undefined,
      outlookEventSubject: undefined,
      outlookEventSuccessful: undefined,
    };
    this.createEvent(element.rootId, newEvent, options);
  }

  createEvent(rootId: number, event: EmployerEvent = null, options: EmployerEventOptions = null): void {
    const eventDialogConfig = new MatDialogConfig();
    eventDialogConfig.width = '650px';
    eventDialogConfig.autoFocus = false;
    eventDialogConfig.disableClose = true;

    eventDialogConfig.data = {
      mode: EventModalMode.create,
      event: event,
      staffId: this.staffId,
      rootId: rootId,
      eventOptions: options,
      permissions: {
        canEdit: this.permissions.canEditEvent,
        canDelete: this.permissions.canDeleteEvent,
      },
      showBranch: true,
    };
    this.loadingEvent = null;
    this.cdr.detectChanges();

    this.dialog
      .open(EmployerEventsFormComponent, eventDialogConfig)
      .afterClosed()
      .subscribe((event) => {
        if (event?.refresh === true) {
          this.getEvents();
        }
        this.openEvent = false;
      });
  }

  viewEvent(eventId: number, options: EmployerEventOptions = null): void {
    const subscription = this.employerService.getEvent(eventId, this.staffId).subscribe({
      next: (data: EmployerEvent) => {
        const event: EmployerEvent = data;

        const eventDialogConfig = new MatDialogConfig();
        eventDialogConfig.width = '650px';
        eventDialogConfig.autoFocus = false;
        eventDialogConfig.disableClose = true;

        eventDialogConfig.data = {
          mode: EventModalMode.view,
          event: event,
          staffId: this.staffId,
          employerId: event?.branchId,
          eventOptions: options,
          permissions: {
            canEdit: this.permissions.canEditEvent,
            canDelete: this.permissions.canDeleteEvent,
          },
          showBranch: true,
        };
        this.loadingEvent = null;
        this.cdr.detectChanges();

        this.dialog
          .open(EmployerEventsFormComponent, eventDialogConfig)
          .afterClosed()
          .subscribe((event) => {
            if (event?.refresh === true) {
              this.getEvents();
            }
            this.openEvent = false;
          });
      },
      error: (error) => {
        console.error(error);
        subscription.unsubscribe();
        this.openEvent = false;
        this.loadingEvent = null;
        this.snackBar.open(this.dataError, '', {
          duration: 2000,
        });
      },
      complete: () => {
        subscription.unsubscribe();
      },
    });
  }

  changeTab(tab: string): void {
    if (tab === this.activeTab) {
      return;
    }

    this.activeTab = tab;
    window.history.pushState({}, '', `hs-homepage?tab=${tab.toLowerCase().replace(' ', '-')}`);

    // Set date filter to 6 months ago for Overdue tab - we don't want to get all time data
    if (tab === 'Overdue') {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      this.filterDateFrom = sixMonthsAgo;
      this.filterDateChanged.emit(sixMonthsAgo);
    }

    // Set date filter to today for Upcoming and ELI tabs
    if (tab === 'Upcoming' || tab === 'ELI') {
      const today = new Date();
      this.filterDateFrom = today;
      this.filterDateChanged.emit(today);
    }

    if (tab === 'ELI') {
      this.getELI();
    } else if (tab === 'Missing Appraisal') {
      this.getMissingAppraisal();
    } else if (tab === 'Errors') {
      this.getErrors();
    } else {
      this.getEvents();
    }

    this.tabChanged.emit(this.activeTab);
  }

  today = new Date().setHours(0, 0, 0, 0);

  PlannedOverDueDate(planned: string | null, due: string | null): boolean {
    const plannedDate = new Date(planned).setHours(0, 0, 0, 0);
    const dueDate = new Date(due).setHours(0, 0, 0, 0);

    if (planned === null || due === null) {
      return false;
    }

    return plannedDate > dueDate;
  }
}
